import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserTag, UserType } from '../UserTag/UserTag';
import { UserTypeInfo } from '../UserTypeInfo/UserTypeInfo';
import { CalendarIcon, LocationMarkerIcon } from '../icons';
import { DefaultAvatar } from '../icons/DefaultAvatar';
import './PhotoGallery.css';

export interface PhotoMetadata {
  userName: string;
  userAvatarUrl?: string;
  timestamp: string;
  userTags: UserType[];
  /** URL path to the user's profile (e.g. "/musician-profile" or "/fan-profile") */
  userProfileUrl?: string;
  eventInfo?: {
    artistName: string;
    artistAvatarUrl?: string;
    date: string;
    venue: string;
  };
}

export interface PhotoOverlayInfoProps {
  /** Show the user info lockup (avatar, name, timestamp, badges) */
  showUserInfo?: boolean;
  /** Show the event info lockup (artist, date, venue) */
  showEventInfo?: boolean;
  /** Metadata for the current photo */
  metadata?: PhotoMetadata;
}

/**
 * PhotoOverlayInfo — bottom-left overlay for the fullscreen photo lightbox.
 *
 * Two independently toggleable lockups stacked vertically:
 *   1. User info (mirrors spec-comment__top-info without the comment text)
 *   2. Event info (mirrors spec-comment__event-info)
 */
export const PhotoOverlayInfo: React.FC<PhotoOverlayInfoProps> = ({
  showUserInfo = false,
  showEventInfo = false,
  metadata,
}) => {
  const navigate = useNavigate();
  const [activeTagType, setActiveTagType] = useState<UserType | null>(null);

  if ((!showUserInfo && !showEventInfo) || !metadata) return null;

  const resolvedTags: UserType[] =
    metadata.userTags.length > 0 ? metadata.userTags : ['noAccount'];

  const handleContainerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleTagClick = (tagType: UserType) => {
    setActiveTagType(tagType);
  };

  const handleUserClick = (e: React.MouseEvent) => {
    if (metadata.userProfileUrl) {
      e.stopPropagation();
      navigate(metadata.userProfileUrl);
    }
  };

  return (
    <>
      <div className="photo-overlay-info" onClick={handleContainerClick}>
        {/* Lockup 1 — User info (top-info without comment text) */}
        {showUserInfo && (
          <div className="photo-overlay-info__user">
            {/* Header row: avatar + name + timestamp */}
            <div className="photo-overlay-info__header">
              <div
                className={`photo-overlay-info__user-detail ${metadata.userProfileUrl ? 'photo-overlay-info__user-detail--clickable' : ''}`}
                onClick={handleUserClick}
                role={metadata.userProfileUrl ? 'link' : undefined}
              >
                {metadata.userAvatarUrl ? (
                  <img
                    src={metadata.userAvatarUrl}
                    alt={metadata.userName}
                    className="photo-overlay-info__avatar"
                  />
                ) : (
                  <div className="photo-overlay-info__avatar">
                    <DefaultAvatar size={14} />
                  </div>
                )}
                <span className="photo-overlay-info__user-name">
                  {metadata.userName}
                </span>
              </div>
              <span className="photo-overlay-info__timestamp">
                {metadata.timestamp}
              </span>
            </div>

            {/* Badges row */}
            <div className="photo-overlay-info__badges">
              {resolvedTags.map((tagType) => (
                <UserTag
                  key={tagType}
                  type={tagType}
                  onClick={() => handleTagClick(tagType)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Lockup 2 — Event info */}
        {showEventInfo && metadata.eventInfo && (
          <div className="photo-overlay-info__event">
            {/* Artist */}
            <div className="photo-overlay-info__event-detail">
              {metadata.eventInfo.artistAvatarUrl ? (
                <img
                  src={metadata.eventInfo.artistAvatarUrl}
                  alt={metadata.eventInfo.artistName}
                  className="photo-overlay-info__event-avatar"
                />
              ) : (
                <div className="photo-overlay-info__event-avatar">
                  <DefaultAvatar size={16} />
                </div>
              )}
              <span className="photo-overlay-info__event-text">
                {metadata.eventInfo.artistName}
              </span>
            </div>

            {/* Date */}
            <div className="photo-overlay-info__event-detail">
              <CalendarIcon size="s" tone="darkGrey" />
              <span className="photo-overlay-info__event-text">
                {metadata.eventInfo.date}
              </span>
            </div>

            {/* Venue */}
            <div className="photo-overlay-info__event-detail">
              <LocationMarkerIcon size="s" tone="darkGrey" />
              <span className="photo-overlay-info__event-text">
                {metadata.eventInfo.venue}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* UserTypeInfo overlay — renders on top of lightbox */}
      {activeTagType && (
        <UserTypeInfo
          type={activeTagType}
          open={true}
          onClose={() => setActiveTagType(null)}
        />
      )}
    </>
  );
};

/**
 * PhotoThumbnailInfo — small avatar + name overlay for photo thumbnails.
 * Sits at bottom-left of the thumbnail, 8px inset. Scales down on small screens.
 */
export interface PhotoThumbnailInfoProps {
  metadata?: PhotoMetadata;
}

export const PhotoThumbnailInfo: React.FC<PhotoThumbnailInfoProps> = ({
  metadata,
}) => {
  const navigate = useNavigate();

  if (!metadata) return null;

  const handleClick = (e: React.MouseEvent) => {
    if (metadata.userProfileUrl) {
      e.stopPropagation();
      navigate(metadata.userProfileUrl);
    }
  };

  return (
    <div className={`photo-thumb-info ${metadata.userProfileUrl ? 'photo-thumb-info--clickable' : ''}`}>
      <div
        className={`photo-thumb-info__detail ${metadata.userProfileUrl ? 'photo-thumb-info__detail--clickable' : ''}`}
        onClick={handleClick}
        role={metadata.userProfileUrl ? 'link' : undefined}
      >
        {metadata.userAvatarUrl ? (
          <img
            src={metadata.userAvatarUrl}
            alt={metadata.userName}
            className="photo-thumb-info__avatar"
          />
        ) : (
          <div className="photo-thumb-info__avatar">
            <DefaultAvatar size={10} />
          </div>
        )}
        <span className="photo-thumb-info__name">{metadata.userName}</span>
      </div>
    </div>
  );
};

export default PhotoOverlayInfo;
